import type { TableColumnDefinition, OnSelectionChangeData} from "@fluentui/react-components";
import { createTableColumn, TableCellLayout, Toolbar, Tooltip, ToolbarButton, useScrollbarWidth, useFluent } from "@fluentui/react-components";
import type { EAppId, ISetting, SettingsData } from "../../interfaces";
import { AddRegular, ArrowExportRegular, ArrowImportRegular, DeleteRegular, EditRegular } from "@fluentui/react-icons";
import { useEffect, useState } from "react";
import { AddDialog } from "./AddDialog/AddDialog";
import { EditDialog } from "./EditDialog/EditDialog";
import type {
    RowRenderer
} from '@fluentui-contrib/react-data-grid-react-window';
import {
    DataGridBody,
    DataGrid,
    DataGridRow,
    DataGridHeader,
    DataGridCell,
    DataGridHeaderCell
} from '@fluentui-contrib/react-data-grid-react-window';
import { exportSettingsData, importSettingsDataAsync } from "../../utilities/ImportExportHelper/ImportExportHelper";
import { publishCustomError } from "../../events";
import { getAvailableConfigurationEntities, getSettingArrayFromSettingsData, getDisplayValueForSetting } from "../../utilities/ConfigurationData/ConfigurationData";

export interface ISettingsEditorProps {
    settingsData?: SettingsData;
    applicationId: EAppId;
    isLoading?: boolean;
    onSettingsUpdate(settings: SettingsData): void;
}

const columns: TableColumnDefinition<ISetting>[] = [
    createTableColumn<ISetting>({
        columnId: "settingsName",
        compare: (a, b) => {
            return a.name.localeCompare(b.name);
        },
        renderHeaderCell: () => {
            return "Name";
        },
        renderCell: (item) => {
            return (
                <TableCellLayout truncate>
                    {item.name}
                </TableCellLayout>
            );
        },
    }),
    createTableColumn<ISetting>({
        columnId: "settingsValue",
        compare: (a, b) => {
            if (typeof a.value === 'string' && typeof b.value === 'string') {
                return a.value.localeCompare(b.value);
            }

            if (typeof a.value === 'number' && typeof b.value === 'number') {
                return a.value - b.value;
            }

            return String(a.value).localeCompare(String(b.value));
        },
        renderHeaderCell: () => {
            return "Value";
        },
        renderCell: (item) => {
            return (
                <TableCellLayout style={{
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    wordBreak: "break-all",
                    whiteSpace: "normal",
                }} >
                    {getDisplayValueForSetting(item)}
                </TableCellLayout>
            );
        },
    })
];

const renderRow: RowRenderer<ISetting> = ({ item, rowId }, style) => (
    <DataGridRow<ISetting>
        key={rowId} style={style} selectionCell={{ radioIndicator: { "aria-label": "Select row" } }}>
        {({ renderCell }) => (<DataGridCell>{renderCell(item)}</DataGridCell>)}
    </DataGridRow>
);

const GetRowId = (item: ISetting) => { return `${item.key}`; };

const getDataGridHeight = (): number => {
    const windowDataGridMargin = 420;
    const minWindowSize = 500;
    const currentWindowHeight = window.innerHeight > minWindowSize ? window.innerHeight : minWindowSize;
    return currentWindowHeight - windowDataGridMargin;
};

export const SettingsEditor = (props: ISettingsEditorProps) => {
    const { settingsData, isLoading, applicationId, onSettingsUpdate } = props;
    const { targetDocument } = useFluent();
    const scrollbarWidth = useScrollbarWidth({ targetDocument });

    const [selectedSettingsKey, setSelectedSettingsKey] = useState("");
    const [currentSettingsValue, setCurrentSettingsValue] = useState<string | number | boolean>("");
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [datGridHeight, setDataGridHeight] = useState(getDataGridHeight());

    const items: ISetting[] = getSettingArrayFromSettingsData(applicationId, settingsData);

    useEffect(() => {
        function handleResize() {
            setDataGridHeight(getDataGridHeight());
        }

        window.addEventListener('resize', handleResize);
        return function cleanupListener() {
            window.removeEventListener('resize', handleResize);
        };
    });

    const onSettingsSelectionChanged = (data: OnSelectionChangeData): void => {
        setShowAddDialog(false);
        setSelectedSettingsKey(String(data.selectedItems.values().next().value ?? ""));
    };

    const onEdit = (): void => {
        setCurrentSettingsValue(settingsData ? settingsData[selectedSettingsKey] : "");
        setShowEditDialog(true);
    };

    const onAdd = (): void => {
        setShowAddDialog(true);
    };

    const onRemove = (): void => {
        try {
            const updatedSettings = { ...settingsData };
            Reflect.deleteProperty(updatedSettings, selectedSettingsKey);
            onSettingsUpdate(updatedSettings);
            setSelectedSettingsKey("");
        } catch (error) {
            console.debug(error);
        }
    };

    const onAddCancelled = (): void => {
        setShowAddDialog(false);
    };

    const onEditCancelled = (): void => {
        setShowEditDialog(false);
    };

    const onSettingUpdated = (value: string | number | boolean): void => {
        setShowEditDialog(false);
        setCurrentSettingsValue(value);
        const updatedSettings = { ...settingsData, [selectedSettingsKey]: value };
        onSettingsUpdate(updatedSettings);
    };

    const onSettingAdded = (data: SettingsData): void => {
        setShowAddDialog(false);
        const updatedSettings = { ...settingsData, ...data };
        onSettingsUpdate(updatedSettings);
    };

    const onExportSettings = (): void => {
        if (settingsData) {
            exportSettingsData(applicationId, settingsData);
        }
    };

    const onImportSettings = async (): Promise<void> => {
        try {
            const importedData = await importSettingsDataAsync(applicationId);
            if (importedData) {
                onSettingsUpdate(importedData);
            }
        } catch (error) {
            console.error(error);
            if (error instanceof Error) {
                publishCustomError(error.message);
            } else if (typeof error === "string") {
                publishCustomError(String(error));
            }
        }
    };

    const options = getAvailableConfigurationEntities(applicationId, Object.keys(settingsData ?? []));
    const allOptions = getAvailableConfigurationEntities(applicationId);
    const configurationEntity = allOptions.find(x => x.key === selectedSettingsKey);

    const columnSizingOptions = {
        settingsName: {
            minWidth: 100,
            defaultWidth: 100,
            idealWidth: 280,
        }
    };

    return (
        <div>
            {showAddDialog &&
                <AddDialog
                    configurationEntities={options}
                    onAdd={onSettingAdded}
                    onCancel={onAddCancelled}
                >
                </AddDialog>}
            {showEditDialog && configurationEntity &&
                <EditDialog
                    configurationEntity={configurationEntity}
                    value={currentSettingsValue}
                    onCancel={onEditCancelled}
                    onUpdate={onSettingUpdated}>
                </EditDialog>}
            <Toolbar aria-label="transparent" >
                <Tooltip
                    content="Add setting"
                    relationship="description"
                    withArrow>
                    <ToolbarButton
                        disabled={isLoading || options.length < 1}
                        icon={<AddRegular />}
                        aria-label="Add setting"
                        onClick={() => onAdd()}>
                        Add
                    </ToolbarButton>
                </Tooltip>
                <Tooltip
                    content="Remove setting"
                    relationship="description"
                    withArrow>
                    <ToolbarButton
                        disabled={!selectedSettingsKey || isLoading}
                        icon={<DeleteRegular />}
                        aria-label="Remove setting"
                        onClick={() => onRemove()}>
                        Remove
                    </ToolbarButton>
                </Tooltip>
                <Tooltip
                    content="Edit setting"
                    relationship="description"
                    withArrow>
                    <ToolbarButton
                        disabled={!selectedSettingsKey || isLoading}
                        icon={<EditRegular />}
                        aria-label="Edit setting"
                        onClick={() => onEdit()}>
                        Edit
                    </ToolbarButton>
                </Tooltip>
                <Tooltip
                    content="Export settings"
                    relationship="description"
                    withArrow>
                    <ToolbarButton
                        disabled={!settingsData || isLoading || Object.entries(settingsData).length < 1}
                        icon={<ArrowExportRegular />}
                        aria-label="Export setting"
                        onClick={() => onExportSettings()}>
                        Export
                    </ToolbarButton>
                </Tooltip>
                <Tooltip
                    content="Import settings"
                    relationship="description"
                    withArrow>
                    <ToolbarButton
                        disabled={isLoading}
                        icon={<ArrowImportRegular />}
                        aria-label="Import setting"
                        onClick={() => onImportSettings()}>
                        Import
                    </ToolbarButton>
                </Tooltip>
            </Toolbar>
            <DataGrid
                onSelectionChange={(_, data) => onSettingsSelectionChanged(data)}
                items={items}
                columns={columns}
                sortable
                resizableColumns
                columnSizingOptions={columnSizingOptions}
                selectionMode="single"
                getRowId={GetRowId}>
                <DataGridHeader style={{ paddingRight: scrollbarWidth }}>
                    <DataGridRow>
                        {({ renderHeaderCell }) => (
                            <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                        )}
                    </DataGridRow>
                </DataGridHeader>
                <DataGridBody<ISetting> itemSize={50} height={datGridHeight}  >
                    {renderRow}
                </DataGridBody>
            </DataGrid>
        </div>
    );
};